/**
 * Import Step
 * 
 * Execute the import with progress tracking
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  LinearProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload as ImportIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import axios from 'axios';

interface ImportStepProps {
  sessionId: string;
  mappings: Record<string, string>;
  validationResult: any;
  onImportComplete: (result: any) => void;
  onError: (error: string) => void;
  onBack: () => void;
}

const ImportStep: React.FC<ImportStepProps> = ({
  sessionId,
  mappings,
  validationResult,
  onImportComplete,
  onError,
  onBack
}) => {
  const [importing, setImporting] = useState(false);
  const [skipInvalid, setSkipInvalid] = useState(true);
  const [importStarted, setImportStarted] = useState(false);

  const hasErrors = validationResult && validationResult.invalid_rows > 0;

  const handleStartImport = async () => {
    setImporting(true);
    setImportStarted(true);

    try {
      const response = await axios.post(
        '/api/v1/import/import',
        {
          session_id: sessionId,
          mappings: mappings,
          skip_invalid: skipInvalid
        }
      );

      if (response.data.success) {
        onImportComplete(response.data.import_result);
      } else {
        onError('Import failed. Please try again.');
      }
    } catch (error: any) {
      console.error('Import failed:', error);
      onError(error.response?.data?.detail || 'Import failed. Please try again.');
      setImporting(false);
      setImportStarted(false);
    }
  };

  if (importStarted && importing) {
    return (
      <Box textAlign="center" py={6}>
        <CircularProgress size={80} sx={{ mb: 3 }} />
        <Typography variant="h6" gutterBottom>
          Importing Your Data...
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Please wait while we import your data into the system.
          This may take a few moments depending on the file size.
        </Typography>
        <LinearProgress sx={{ mt: 3, maxWidth: 400, mx: 'auto' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Ready to Import
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Review the summary below and click "Start Import" when ready.
      </Typography>

      {/* Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          <strong>Import Summary</strong>
        </Typography>
        
        <Box display="flex" flexDirection="column" gap={1} mt={2}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2">Total Rows:</Typography>
            <Typography variant="body2">
              <strong>{validationResult.total_rows}</strong>
            </Typography>
          </Box>
          
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="success.main">
              Valid Rows:
            </Typography>
            <Typography variant="body2" color="success.main">
              <strong>{validationResult.valid_rows}</strong>
            </Typography>
          </Box>
          
          {hasErrors && (
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="error.main">
                Invalid Rows:
              </Typography>
              <Typography variant="body2" color="error.main">
                <strong>{validationResult.invalid_rows}</strong>
              </Typography>
            </Box>
          )}
          
          {validationResult.warnings && validationResult.warnings.length > 0 && (
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="warning.main">
                Warnings:
              </Typography>
              <Typography variant="body2" color="warning.main">
                <strong>{validationResult.warnings.length}</strong>
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Options */}
      {hasErrors && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'warning.lighter' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your data contains {validationResult.invalid_rows} invalid rows.
          </Alert>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={skipInvalid}
                onChange={(e) => setSkipInvalid(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  <strong>Skip invalid rows and import valid data only</strong>
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Invalid rows will be skipped. You can fix and re-import them later.
                </Typography>
              </Box>
            }
          />
        </Paper>
      )}

      {/* What will happen */}
      <Box sx={{ p: 2, bgcolor: 'info.lighter', borderRadius: 1, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          📋 What will happen:
        </Typography>
        <Typography variant="body2" component="div">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {skipInvalid && hasErrors ? (
              <>
                <li>{validationResult.valid_rows} valid rows will be imported</li>
                <li>{validationResult.invalid_rows} invalid rows will be skipped</li>
              </>
            ) : (
              <li>{validationResult.total_rows} rows will be imported</li>
            )}
            <li>Data will be added to your database</li>
            <li>You'll see a detailed results summary</li>
            <li>The import cannot be undone (but you can delete records later)</li>
          </ul>
        </Typography>
      </Box>

      {/* Warning for no skip */}
      {hasErrors && !skipInvalid && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>Warning:</strong> Import will fail if any row has errors. 
          We recommend enabling "Skip invalid rows" option.
        </Alert>
      )}

      {/* Navigation Buttons */}
      <Box display="flex" justifyContent="space-between">
        <Button onClick={onBack} disabled={importing}>
          Back to Validation
        </Button>
        <Button
          variant="contained"
          onClick={handleStartImport}
          disabled={importing}
          startIcon={importing ? <CircularProgress size={20} /> : <ImportIcon />}
          color={hasErrors && !skipInvalid ? 'error' : 'primary'}
          size="large"
        >
          {importing ? 'Importing...' : 'Start Import'}
        </Button>
      </Box>
    </Box>
  );
};

export default ImportStep;

